import { useEffect, useState } from "react";
import "./currentTimeLine.css";

/**
 * Thin horizontal line + left-side time pill marking the current local time
 * inside a calendar timeline grid. Designed for absolute-positioned use
 * inside the same scroll container that holds the hour grid lines and
 * event blocks (DayModal / WeekModal / DayViewModal).
 *
 * Updates once per minute. The first tick aligns to the next :00 of the
 * minute so the displayed value flips on the wall clock; subsequent ticks
 * are plain 60s setInterval. No auto-scroll behavior.
 *
 * Render gating:
 *   - Time-of-day: only renders when local now is within
 *     [gridStartHour, gridStartHour + gridHours). With the default
 *     6 AM × 24h grid this is always true; the gate is here for safety
 *     and future grid changes.
 *   - Date: caller passes `visible` true only if at least one displayed
 *     day is today.
 *
 * Hover dim: when `dimmed` is true (driven by the parent's
 * `useCalendarHoverHighlight().isHoverActive`), the line drops to
 * `opacity: 0.4` to match the non-hovered block alpha so the hover
 * focus stays dominant. Per spec the BUBBLE stays at full opacity.
 */
export default function CurrentTimeLine({
  hourHeight,
  gridStartHour,
  gridHours,
  hourLabelWidth,
  visible = true,
  dimmed = false,
}) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    let intervalId = null;
    const aligned = new Date();
    aligned.setSeconds(0, 0);
    aligned.setMinutes(aligned.getMinutes() + 1);
    const initialDelay = aligned.getTime() - Date.now();
    const timeoutId = setTimeout(() => {
      setNow(new Date());
      intervalId = setInterval(() => setNow(new Date()), 60_000);
    }, Math.max(0, initialDelay));
    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  if (!visible) return null;

  const hourFloat = now.getHours() + now.getMinutes() / 60;
  const gridEnd = gridStartHour + gridHours;
  if (hourFloat < gridStartHour || hourFloat >= gridEnd) return null;

  const top = (hourFloat - gridStartHour) * hourHeight;
  const label = formatTime(now);

  return (
    <div
      className="current-time-line"
      style={{ top: `${top - 1}px` }}
      aria-hidden="true"
    >
      <div
        className="current-time-line__bar"
        style={{
          left: `${hourLabelWidth}px`,
          opacity: dimmed ? 0.4 : 1,
        }}
      />
      <div className="current-time-line__pill" style={{ left: 2 }}>
        {label}
      </div>
    </div>
  );
}

function formatTime(d) {
  let h = d.getHours();
  const ampm = h < 12 ? "AM" : "PM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  const m = String(d.getMinutes()).padStart(2, "0");
  return `${h}:${m} ${ampm}`;
}
